import React from "react";
import {
    Box,
    Typography,
    FormGroup,
    FormControlLabel,
    Checkbox,
    CircularProgress,
} from "@material-ui/core";
import { useMutation, useQuery } from "@apollo/client";
import {
    AddSchoolPermissionDocument,
    AddSchoolPermissionMutation,
    GetSchoolFeatureFlagsDocument,
    GetSchoolFeatureFlagsQuery,
    RemoveSchoolPermissionDocument,
    RemoveSchoolPermissionMutation,
} from "../../../generated/graphql";
import { useRequestError } from "../../../hooks";

interface SchoolFeatureFlagsProps {
    schoolId: string;
}

export const SchoolFeatureFlags: React.FC<SchoolFeatureFlagsProps> = ({ schoolId }) => {
    const {
        data,
        loading: flagsLoading,
        error: flagsError,
        refetch,
    } = useQuery<GetSchoolFeatureFlagsQuery>(GetSchoolFeatureFlagsDocument, {
        variables: { schoolId },
        fetchPolicy: "network-only",
    });

    const [addPermission, { loading: addPermissionLoading, error: addPermissionError }] =
        useMutation<AddSchoolPermissionMutation>(AddSchoolPermissionDocument);

    const [removePermission, { loading: removePermissionLoading, error: removePermissionError }] =
        useMutation<RemoveSchoolPermissionMutation>(RemoveSchoolPermissionDocument);

    const error = flagsError || addPermissionError || removePermissionError;
    const { requestError, setRequestError } = useRequestError(error);

    const isLoading = flagsLoading || addPermissionLoading || removePermissionLoading;

    const handlePermissionChange = async (flagId: string, isGranted: boolean) => {
        try {
            if (isGranted) {
                await addPermission({
                    variables: {
                        schoolId,
                        flagId,
                    },
                });
            } else {
                await removePermission({
                    variables: {
                        schoolId,
                        flagId,
                    },
                });
            }
            refetch();
        } catch (e) {
            console.error("Ошибка при изменении разрешения:", e);
            setRequestError(true);
        }
    };

    const errorMessage =
        (flagsError && "При загрузке списка разрешений произошла ошибка") ||
        (addPermissionError && "При добавлении разрешения произошла ошибка") ||
        (removePermissionError && "При удалении разрешения произошла ошибка");

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
            </Box>
        );
    }

    const featureFlags = data?.getSchoolFeatureFlags || [];

    return (
        <Box mt={2}>
            <Typography variant="h6" gutterBottom>
                Настройки доступов школы
            </Typography>

            {featureFlags.length === 0 ? (
                <Typography color="textSecondary">Доступных флагов функций не найдено</Typography>
            ) : (
                <FormGroup>
                    {featureFlags.map((featureFlag) => (
                        featureFlag && (
                            <FormControlLabel
                                key={featureFlag.id}
                                control={
                                    <Checkbox
                                        checked={featureFlag.isGranted ?? false}
                                        onChange={(e) => handlePermissionChange(featureFlag.id!, e.target.checked)}
                                        color="primary"
                                        disabled={isLoading}
                                    />
                                }
                                label={featureFlag.name || featureFlag.flag}
                            />
                        )
                    ))}
                </FormGroup>
            )}

            {requestError && (
                <Typography color="error" variant="body2" gutterBottom>
                    {errorMessage}
                </Typography>
            )}
        </Box>
    );
};
